import { Component, Input, OnInit } from '@angular/core';
import { BlockUI, NgBlockUI }       from 'ng-block-ui';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { BookingsAppointmentTypeModel } from './typings/bookings.appointment-type';
import { BookingsService }              from './bookings.service';
import { CompanyIntegrationsModel }     from '../companies/models/company.integrations';
import { CompanyService }               from '../companies/company.service';
import { CompanySettingsModel }         from '../companies/models/company.settings';
import { MessagesService }              from '../messages/messages.service';
import { SessionService }               from '../sessions/session.service';
import { Table }                        from '../table/types/table';
import { TableQueryParameters }         from '../table/types/table-query-parameters';
import { TableService }                 from '../table/table.service';

import vvGarageIcon from 'icons/vv-garage.svg';

@Component({
  selector   : 'wor-bookings-settings',
  template: require('./bookings.settings.component.html')
})
export class BookingsSettingsComponent implements OnInit {
  googleBookingUrl = '';
  logoOptions      : any;
  publicUrl        = '';
  tableCacheKey    = this.bookingsService.appointmentsTypesTableCacheKey;

  tableSettings : Table = {
    cache: {
      enabled: true,
      key    : this.tableCacheKey
    },
    columns: [
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        field : 'estimated_hours',
        header: {
          text: this.translateService.instant('BOOKING_SPACE.ESTIMATED_HOURS')
        }
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_BOOKING_TYPES_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 20, 50 ],
      queryCount: () => this.bookingsService.getAppointmentTypesCount()
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: {
      enabled: false
    },
  };

  public vvGarageIcon = vvGarageIcon;

  @BlockUI('bookingsSettingsBlock') block: NgBlockUI;

  @Input() appointmentTypes: Array<BookingsAppointmentTypeModel> = [];
  @Input() maskedCompanyId = '';
  @Input() integrations    : CompanyIntegrationsModel;
  @Input() settings        : CompanySettingsModel;

  constructor (
    private bookingsService : BookingsService,
    public  companyService  : CompanyService,
    private messagesService : MessagesService,
    private router          : UIRouter,
    private sessionService  : SessionService,
    private tableService    : TableService,
    private translateService: TranslateService
  ) {}

  ngOnInit () : void {
    this.logoOptions = {
      accept: 'images',
      file  : this.settings.public_booking_diary_logo,
      size  : 200000,
      type  : 'pbd_logo'
    };

    this.publicUrl = `${
        this.sessionService.base().origin
      }bookings.html#/${
        this.settings.public_booking_diary_company_url
      }?token=${
        this.settings.public_booking_diary_token
      }`;

    // 'google' is the agreed source marker booking attribution records — the link is
    // handed out pre-tagged, so pasting it into Google needs no extra step. It must sit
    // after the hash, alongside the token, or it never reaches the booking page.
    this.googleBookingUrl = `${ this.publicUrl }&source=google`;

    this.settings.masked_company_id = this.maskedCompanyId;
  }

  addAllAppointmentsToVVGarage () : void {
    this.bookingsService.addAllAppointmentsToVVGarage()
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.APPOINTMENTS_ADDED_TO_VV_GARAGE'), 'success'))
    .catch(error => this.messagesService.error(error));
  }

  addAppointmentType () : void {
    this.bookingsService.addAppointmentType()
    .then(( message : string ) => {
      this.messagesService.show(message, 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  getAppointmentTypes ( params : TableQueryParameters ) : Promise<Array<BookingsAppointmentTypeModel>> {
    return this.bookingsService.getAppointmentTypes(params)
      .then(appointmentTypes => {
        this.appointmentTypes = appointmentTypes;

        return appointmentTypes;
      });
  }

  isBrandingAvailable () : boolean {
    return !this.companyService.isBrand('bosch')
      || this.companyService.isBrandPuppet('bosch');
  }

  refresh () : void {
    this.router.stateService.reload();

    this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.REFRESHED') , 'success');
  }

  save () : void {
    this.block.start();

    this.bookingsService.updateSettings(this.settings)
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.SETTINGS'), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  select ( event : any ) : void {
    this.bookingsService.addAppointmentType(event.row)
    .then(( message : string ) => {
      this.messagesService.show(message, 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }
}
